import React, { useEffect, useRef, useState } from 'react';
import { Box, Alert } from '@mantine/core';

const PlyrPlayer = ({ videoPath, title = "Course Video", autoplay = false }) => {
  const playerRef = useRef(null);
  const videoRef = useRef(null);
  const loadingOverlayRef = useRef(null);
  const [error, setError] = useState(null);
  const [isBuffering, setIsBuffering] = useState(true);

  // Construct the HLS URL
  const videoUrl = `https://manifestationmagic.samrruddhi.in/videos/mm/${videoPath}optimized.mp4/index.m3u8`;

  useEffect(() => {
    const initializePlayer = async () => {
      try {
        // Dynamic imports
        const Plyr = (await import('plyr')).default;
        const Hls = (await import('hls.js')).default;

        if (videoRef.current && !playerRef.current) {
          const video = videoRef.current;

          // Initialize HLS if supported
          if (Hls.isSupported()) {
            const hls = new Hls({
              enableWorker: true,
              lowLatencyMode: false,
              backBufferLength: 90
            });

            hls.loadSource(videoUrl);
            hls.attachMedia(video);

            hls.on(Hls.Events.ERROR, (event, data) => {
              if (data.fatal) {
                console.error('HLS Error:', data);
                setError('Failed to load video stream');
              }
            });

            // Store hls instance for cleanup
            video.hlsInstance = hls;
          } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
            // Native HLS support
            video.src = videoUrl;
          } else {
            setError('HLS streaming not supported on this browser');
            return;
          }

          // Detect if device is mobile
          const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
          
          // Initialize Plyr with mobile-optimized controls
          const player = new Plyr(video, {
            controls: isMobile ? [
              'play-large',
              'play',
              'progress', 
              'current-time',
              'duration',
              'settings',
              'fullscreen'
            ] : [
              'play-large',
              'play',
              'progress', 
              'current-time',
              'duration',
              'mute',
              'volume',
              'settings',
              'fullscreen'
            ],
            settings: ['quality', 'speed'],
            speed: { selected: 1, options: [0.5, 0.75, 1, 1.25, 1.5, 2] },
            autoplay: autoplay,
            hideControls: true,
            resetOnEnd: false,
            keyboard: { focused: true, global: false },
            tooltips: { controls: true, seek: true },
            displayDuration: true,
            storage: { enabled: false }, // Disable storage to prevent issues
            fullscreen: { 
              enabled: true, 
              fallback: true, 
              iosNative: true,
              container: null // Use the default container
            }
          });

          playerRef.current = player;

          // Event listeners
          player.on('ready', () => {
            console.log('Plyr player is ready');
            
            // Auto-rotate to landscape on mobile when entering fullscreen
            if (isMobile && 'screen' in window && 'orientation' in window.screen) {
              player.on('enterfullscreen', () => {
                if (window.screen.orientation && window.screen.orientation.lock) {
                  window.screen.orientation.lock('landscape').catch(err => {
                    console.log('Screen orientation lock failed:', err);
                  });
                }
              });
              
              player.on('exitfullscreen', () => {
                if (window.screen.orientation && window.screen.orientation.unlock) {
                  window.screen.orientation.unlock();
                }
              });
            }
          });

          // Enhanced loading/buffering event handling
          player.on('loadstart', () => {
            console.log('Video loading started');
            setIsBuffering(true);
          });

          player.on('waiting', () => {
            console.log('Video is buffering');
            setIsBuffering(true);
          });

          player.on('canplay', () => {
            console.log('Video can start playing');
            setIsBuffering(false);
          });

          player.on('playing', () => {
            console.log('Video is playing');
            setIsBuffering(false);
          });

          player.on('stalled', () => {
            console.log('Video stalled - buffering');
            setIsBuffering(true);
          });

          player.on('seeking', () => {
            console.log('Video seeking');
            setIsBuffering(true);
          });

          player.on('seeked', () => {
            console.log('Video seeked');
            setIsBuffering(false);
          });

          // HLS specific events for better buffering detection
          if (video.hlsInstance) {
            video.hlsInstance.on('hlsBufferStalled', () => {
              console.log('HLS buffer stalled');
              setIsBuffering(true);
            });

            video.hlsInstance.on('hlsBufferFlushed', () => {
              console.log('HLS buffer flushed');
              setIsBuffering(false);
            });
          }

          player.on('error', (event) => {
            console.error('Plyr error:', event);
            setError('Video playback error occurred');
          });
        }
      } catch (err) {
        console.error('Error initializing Plyr:', err);
        setError('Failed to initialize video player');
      }
    };

    initializePlayer();

    return () => {
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
      if (videoRef.current && videoRef.current.hlsInstance) {
        videoRef.current.hlsInstance.destroy();
      }
    };
  }, [videoUrl, autoplay]);

  if (error) {
    return (
      <Alert color="red" title="Video Error">
        {error}
      </Alert>
    );
  }

  return (
    <Box style={{ width: '100%', position: 'relative' }}>
      {/* Plyr CSS and Custom Styling */}
      <style jsx>{`
        @import url('https://cdn.plyr.io/3.7.8/plyr.css');

        /* Custom theme with your accent color #7243ec */
        .plyr {
          border-radius: 8px;
          overflow: hidden;
        }

        .plyr--video {
          background: #000;
        }

        .plyr__control--overlaid {
          background: rgba(114, 67, 236, 0.9) !important;
          border: 2px solid #7243ec !important;
          border-radius: 50% !important;
        }

        .plyr__control--overlaid:hover {
          background: rgba(114, 67, 236, 1) !important;
        }

        .plyr__progress__buffer {
          background: rgba(255, 255, 255, 0.25) !important;
        }

        .plyr__progress--seek {
          background: rgba(255, 255, 255, 0.3) !important;
        }

        .plyr__progress--played {
          background: #7243ec !important;
        }

        .plyr__volume--display {
          background: #7243ec !important;
        }

        .plyr__control svg {
          fill: white !important;
        }

        .plyr__control:hover {
          background: rgba(114, 67, 236, 0.8) !important;
        }

        .plyr__control[aria-pressed="true"] {
          background: rgba(114, 67, 236, 0.9) !important;
          color: white !important;
        }

        .plyr__time {
          color: rgba(255, 255, 255, 0.9) !important;
          font-size: 14px !important;
        }

        .plyr__controls {
          background: linear-gradient(180deg, transparent, rgba(0, 0, 0, 0.8)) !important;
          border-radius: 0 !important;
        }

        .plyr__progress {
          height: 8px !important;
        }

        .plyr__progress input[type="range"] {
          height: 8px !important;
        }

        .plyr__progress__thumb {
          background: #7243ec !important;
          box-shadow: 0 1px 1px rgba(114, 67, 236, 0.15), 0 0 0 1px rgba(114, 67, 236, 0.2) !important;
        }

        /* Custom buffering overlay */
        .buffering-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          z-index: 15;
          backdrop-filter: blur(2px);
        }

        .buffering-spinner {
          width: 60px;
          height: 60px;
          border: 4px solid rgba(255, 255, 255, 0.2);
          border-top: 4px solid #7243ec;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 16px;
        }

        .buffering-text {
          color: white;
          font-size: 16px;
          font-weight: 500;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.8);
          font-family: inherit;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* Mobile buffering adjustments */
        @media (max-width: 768px) {
          .buffering-spinner {
            width: 50px;
            height: 50px;
            border-width: 3px;
            margin-bottom: 12px;
          }

          .buffering-text {
            font-size: 14px;
          }
        }

        /* Mobile buffering adjustments */
        @media (max-width: 768px) {
          .plyr--custom-loading .plyr__poster::after {
            width: 50px;
            height: 50px;
            margin: -25px 0 0 -25px;
            border-width: 3px;
          }

          .plyr--custom-loading .plyr__poster::before {
            top: calc(50% + 35px);
            font-size: 14px;
          }
        }
        @media (max-width: 768px) {
          .plyr__controls {
            padding: 15px 10px 10px 10px !important;
          }

          .plyr__control {
            padding: 12px !important;
            margin: 0 2px !important;
          }

          .plyr__progress {
            height: 10px !important;
            margin-bottom: 12px !important;
          }

          .plyr__progress input[type="range"] {
            height: 10px !important;
          }

          .plyr__time {
            font-size: 16px !important;
          }

          .plyr__control--overlaid {
            padding: 15px !important;
          }

          /* Hide volume controls on mobile completely */
          .plyr__volume {
            display: none !important;
          }

          .plyr__controls .plyr__control[data-plyr="mute"] {
            display: none !important;
          }

          /* Adjust layout without volume controls */
          .plyr__controls {
            grid-template-columns: auto 1fr auto auto !important;
          }
        }

        /* Loading/Buffering Animation */
        .plyr--loading .plyr__progress__buffer {
          animation: plyr-progress 1s linear infinite !important;
          background-size: 25px 25px !important;
          background-image: linear-gradient(
            -45deg,
            rgba(114, 67, 236, 0.6) 25%,
            transparent 25%,
            transparent 50%,
            rgba(114, 67, 236, 0.6) 50%,
            rgba(114, 67, 236, 0.6) 75%,
            transparent 75%,
            transparent
          ) !important;
        }

        @keyframes plyr-progress {
          to {
            background-position: 25px 0;
          }
        }

        /* Custom buffering spinner overlay */
        .plyr__poster {
          background-color: rgba(0, 0, 0, 0.8) !important;
        }

        .plyr--loading .plyr__poster::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 60px;
          height: 60px;
          margin: -30px 0 0 -30px;
          border: 4px solid rgba(255, 255, 255, 0.2);
          border-top: 4px solid #7243ec;
          border-radius: 50%;
          animation: plyr-loading-spin 1s linear infinite;
          z-index: 10;
        }



        /* Buffer indicator in progress bar */
        .plyr__progress--buffer {
          background: rgba(114, 67, 236, 0.3) !important;
          opacity: 0.8 !important;
        }

        /* Loading text indicator */
        .plyr--loading .plyr__poster::before {
          content: 'Buffering...';
          position: absolute;
          top: calc(50% + 45px);
          left: 50%;
          transform: translateX(-50%);
          color: white;
          font-size: 16px;
          font-weight: 500;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.8);
          z-index: 11;
        }

        /* Fullscreen adjustments */
        .plyr--fullscreen-active {
          background: #000 !important;
        }

        /* Tooltip styling */
        .plyr__tooltip {
          background: rgba(114, 67, 236, 0.9) !important;
          color: white !important;
          border-radius: 4px !important;
          font-size: 12px !important;
        }

        /* Settings menu - Fixed background */
        .plyr__menu {
          background: rgba(0, 0, 0, 0.95) !important;
          border-radius: 8px !important;
          border: 1px solid rgba(114, 67, 236, 0.3) !important;
          backdrop-filter: blur(10px) !important;
        }

        .plyr__menu__container {
          background: rgba(0, 0, 0, 0.9) !important;
          border-radius: 6px !important;
        }

        .plyr__menu__container [role="menuitem"] {
          color: white !important;
          background: transparent !important;
        }

        .plyr__menu__container [role="menuitem"]:hover {
          background: rgba(114, 67, 236, 0.8) !important;
          color: white !important;
        }

        .plyr__menu__container [role="menuitem"][aria-checked="true"] {
          color: #7243ec !important;
          background: rgba(114, 67, 236, 0.2) !important;
        }

        .plyr__menu__container .plyr__control {
          color: white !important;
        }

        /* Progress bar positioning fix */
        .plyr__controls {
          background: linear-gradient(180deg, transparent, rgba(0, 0, 0, 0.8)) !important;
          border-radius: 0 !important;
          padding: 15px 15px 10px 15px !important;
        }

        .plyr__progress {
          height: 8px !important;
          margin-bottom: 10px !important;
        }

        .plyr__progress input[type="range"] {
          height: 8px !important;
        }
      `}</style>

      {/* Custom Buffering Overlay */}
      {isBuffering && (
        <div className="buffering-overlay" ref={loadingOverlayRef}>
          <div className="buffering-spinner"></div>
          <div className="buffering-text">Buffering...</div>
        </div>
      )}

      {/* Title overlay */}
      {/* {title && (
        <Box
          style={{
            position: 'absolute',
            top: '16px',
            left: '16px',
            right: '16px',
            color: 'white',
            fontSize: '18px',
            fontWeight: 600,
            textShadow: '0 2px 8px rgba(0,0,0,0.8)',
            zIndex: 20,
            pointerEvents: 'none'
          }}
        >
          {title}
        </Box>
      )} */}

      {/* Video element */}
      <video
        ref={videoRef}
        playsInline
        preload="metadata"
        style={{
          width: '100%',
          aspectRatio: '16/9',
          backgroundColor: '#000'
        }}
        onContextMenu={(e) => e.preventDefault()}
      />
    </Box>
  );
};

export default PlyrPlayer;